import { useEffect, useState } from "react";

import { ErrorHandler } from "@/web/lib/errors";

/**
 * A coarse client location. Today it is only ever derived from IP geolocation
 * (see {@link useClientLocation}); the shape is deliberately source-agnostic so a
 * future precise-geolocation source can resolve to the same thing.
 */
export type ClientLocation = {
  latitude: number;
  longitude: number;
  /** Best-effort place labels from the provider; absent when unknown. */
  city?: string;
  region?: string;
  countryCode?: string;
};

/**
 * Per-source fetch state. `unavailable` is terminal for the current cache
 * window — it covers "the provider had no answer" and "the request was blocked
 * or failed" alike, both of which we deliberately stop retrying for a while.
 */
export type ClientLocationStatus = "loading" | "ready" | "unavailable";

// Keyed by source (and versioned) so a future precise-location source caches
// under its own key without colliding with this IP one. Bump `:v1` if the
// cached shape ever changes.
const IP_CACHE_KEY = "client-location:ip:v1";

// IP geolocation barely moves, so a hit is good for a day. A miss/blocked result
// is remembered for an hour: long enough to avoid hammering ipapi.co (and to
// respect a user who has blocked it), short enough to recover from a blip.
const OK_TTL_MS = 24 * 60 * 60 * 1000;
const ERROR_TTL_MS = 60 * 60 * 1000;

const IPAPI_URL = "https://ipapi.co/json/";

type CacheEntry =
  | { status: "ok"; location: ClientLocation; fetchedAt: number }
  | { status: "error"; fetchedAt: number };

function readCache(): CacheEntry | undefined {
  try {
    const raw = window.localStorage.getItem(IP_CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as CacheEntry;
    const ttl = parsed.status === "ok" ? OK_TTL_MS : ERROR_TTL_MS;
    if (Date.now() - parsed.fetchedAt > ttl) return undefined;
    return parsed;
  } catch (error: unknown) {
    // Malformed JSON, private-mode getItem throw, etc. — treat as a cache miss.
    ErrorHandler.logError(error);
    return undefined;
  }
}

function writeCache(entry: CacheEntry): void {
  try {
    window.localStorage.setItem(IP_CACHE_KEY, JSON.stringify(entry));
  } catch (error: unknown) {
    // Quota exceeded / private mode — caching is best-effort, so just log.
    ErrorHandler.logError(error);
  }
}

// ipapi.co returns coordinates as numbers on success, or `{ error: true, ... }`
// when rate-limited or blocked. We only trust finite coordinates.
type IpapiResponse = {
  latitude?: number;
  longitude?: number;
  city?: string;
  region?: string;
  country_code?: string;
  error?: boolean;
};

function parseIpapi(data: IpapiResponse): ClientLocation | undefined {
  if (data.error) return undefined;
  const { latitude, longitude } = data;
  if (typeof latitude !== "number" || !Number.isFinite(latitude)) return undefined;
  if (typeof longitude !== "number" || !Number.isFinite(longitude)) return undefined;
  return {
    latitude,
    longitude,
    city: data.city,
    region: data.region,
    countryCode: data.country_code,
  };
}

// Shared across every hook instance and across React's dev double-invoke so the
// network is hit at most once per page load (and not at all on a cache hit).
let inFlight: Promise<ClientLocation | undefined> | null = null;

async function fetchIpLocation(): Promise<ClientLocation | undefined> {
  try {
    const res = await fetch(IPAPI_URL, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      writeCache({ status: "error", fetchedAt: Date.now() });
      return undefined;
    }
    const location = parseIpapi((await res.json()) as IpapiResponse);
    writeCache(
      location
        ? { status: "ok", location, fetchedAt: Date.now() }
        : { status: "error", fetchedAt: Date.now() },
    );
    return location;
  } catch (error: unknown) {
    // A blocked request (ad/tracker blocker, offline, CORS) throws here. Remember
    // it so we respect the block and don't retry until the error TTL lapses.
    ErrorHandler.logError(error);
    writeCache({ status: "error", fetchedAt: Date.now() });
    return undefined;
  }
}

function loadIpLocation(): Promise<ClientLocation | undefined> {
  const cached = readCache();
  if (cached) {
    return Promise.resolve(cached.status === "ok" ? cached.location : undefined);
  }
  inFlight ??= fetchIpLocation().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Best-effort client location, fetched from IP geolocation (ipapi.co) and cached
 * in `localStorage` per source with a TTL. Resolution is intentionally coarse and
 * unreliable behind VPNs/proxies; callers must handle `ipLocation === undefined`.
 *
 * The result is namespaced under `ipLocation` so the hook can later grow a
 * `preciseLocation` (e.g. the browser Geolocation API) without breaking callers.
 */
export function useClientLocation(): {
  ipLocation: ClientLocation | undefined;
  ipStatus: ClientLocationStatus;
} {
  const [ipLocation, setIpLocation] = useState<ClientLocation | undefined>(undefined);
  const [ipStatus, setIpStatus] = useState<ClientLocationStatus>("loading");

  useEffect(() => {
    let active = true;
    void loadIpLocation().then((location) => {
      if (!active) return;
      setIpLocation(location);
      setIpStatus(location ? "ready" : "unavailable");
    });
    return () => {
      active = false;
    };
  }, []);

  return { ipLocation, ipStatus };
}
